import { DeleteOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ColorPicker,
  Form,
  Input,
  Modal,
  Popover,
  Space,
  Typography,
} from 'antd';

import { CloseIconsForModalWithDataTestId } from '@app/components';
import { RULES_IS_REQUIRE, TAGS_COLORS } from '@app/constants';
import { IconsPopoverContent } from '@app/pages/Tags/components/TagsPageHeader/components';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { FormInstance } from 'antd';

import '@pages/Tags/styles/AddTagsModal.scss';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface AddTagsModalProps {
  currentTagId: number;
  isModalOpen: boolean;
  handleCancel: () => void;
  form: FormInstance;
  submitMainForm: (isTagRestoring?: boolean) => void;
  setSelectedIcon: React.Dispatch<React.SetStateAction<string>>;
  selectedIcon: string;
  disabled?: boolean;
  isCurrentTagDeleted?: boolean;
}

export const AddTagsModal = ({
  currentTagId,
  isModalOpen,
  handleCancel,
  form,
  submitMainForm,
  setSelectedIcon,
  selectedIcon,
  disabled = false,
  isCurrentTagDeleted = false,
}: AddTagsModalProps) => {
  const handleDelete = () => {
    Modal.confirm({
      title: generateSpanWithDataTestId('Удаление тега', 'delete-tag-title'),
      content: generateSpanWithDataTestId(
        'Вы уверены, что хотите удалить этот тег?',
        'delete-tag-description'
      ),
      okText: generateSpanWithDataTestId('Удалить', 'delete-tag'),
      cancelText: generateSpanWithDataTestId('Отмена', 'cancel-delete'),
      okButtonProps: { danger: true },
      onOk: () => {
        form.setFieldsValue({ IsDeleted: true });
        form.submit();
      },
      wrapClassName: 'delete-tag-popup',
    });
  };

  const handleRestoreTag = () => {
    form.setFieldsValue({ IsDeleted: false });
    form.submit();
  };

  return (
    <Modal
      title={
        <Title
          level={4}
          style={{ margin: '0' }}
          data-testid={currentTagId ? 'edit-tag-title' : 'add-tag-title'}
        >
          {currentTagId ? 'Редактировать тег' : 'Добавить тег'}
        </Title>
      }
      data-testid={currentTagId ? 'edit-tag-popup' : 'add-tag-popup'}
      rootClassName="add-tags-modal"
      closeIcon={<CloseIconsForModalWithDataTestId />}
      open={isModalOpen}
      onOk={form.submit}
      onCancel={handleCancel}
      cancelText="Отменить"
      okText="Сохранить"
      className="modal"
      okButtonProps={{
        'data-testid': 'save',
      }}
      cancelButtonProps={{
        'data-testid': 'cancel',
      }}
      footer={[
        !!currentTagId && !isCurrentTagDeleted && (
          <Button
            key="delete"
            danger
            type="primary"
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            disabled={disabled}
            data-testid="delete"
          >
            Удалить
          </Button>
        ),
        isCurrentTagDeleted && currentTagId && (
          <Button
            key="restore"
            type="primary"
            onClick={handleRestoreTag}
            disabled={disabled}
            data-testid="delete"
          >
            Восстановить
          </Button>
        ),
        <Button
          key="cancel"
          onClick={handleCancel}
          disabled={disabled}
          data-testid="cancel"
        >
          Отменить
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={form.submit}
          disabled={disabled}
          data-testid="save"
        >
          Сохранить
        </Button>,
      ].filter(Boolean)}
    >
      <Form form={form} className="modal-form" onFinish={submitMainForm}>
        <Space direction="vertical" size="large" className="form-space">
          <div className="container">
            <Text className="label-require" strong data-testid="tag-name-title">
              Название
            </Text>

            <Form.Item
              name="Name"
              className="mg-0"
              rules={[...RULES_IS_REQUIRE]}
            >
              <Input maxLength={40} data-testid="tag-name" />
            </Form.Item>
          </div>

          <div className="palette">
            <div className="container">
              <Text strong data-testid="color-title">
                Цвет
              </Text>

              <Form.Item name="Color" className="mg-0">
                <ColorPicker
                  presets={[
                    {
                      label: '',
                      colors: TAGS_COLORS,
                    },
                  ]}
                  data-testid="color-picker"
                />
              </Form.Item>
            </div>

            <div className="container">
              <Text strong data-testid="tag-icon-title">
                Иконка
              </Text>

              <Form.Item className="mg-0">
                <Popover
                  content={
                    <IconsPopoverContent setSelectedIcon={setSelectedIcon} />
                  }
                  trigger="click"
                  placement="bottomLeft"
                >
                  <Button
                    style={{ padding: '0', width: '32px' }}
                    data-testid="icon-picker"
                  >
                    <FontAwesomeIcon icon={selectedIcon as IconProp} />
                  </Button>
                </Popover>
              </Form.Item>
            </div>
          </div>

          <div className="container" style={{ alignItems: 'flex-start' }}>
            <Text strong data-testid="tag-description-title">
              Описание
            </Text>

            <Form.Item name="Description" className="mg-0">
              <TextArea
                rows={4}
                maxLength={260}
                data-testid="tag-description"
              />
            </Form.Item>
          </div>

          <Form.Item name="IsDeleted" hidden>
            <Input />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};
